import { ChevronLeftIcon, ChevronRightIcon } from './icons'
import './Pagination.css'

interface PaginationProps {
    itemsPerPage: number
    onChangePage: (page: number) => void
    page: number
    total: number
}

interface CursorPaginationProps {
    prevCursor: string | undefined
    nextCursor: string | undefined
    onPrev: (cursor: string | undefined) => void
    onNext: (cursor: string | undefined) => void
}

type PageItem = number | '...'

export function Pagination({
    itemsPerPage,
    onChangePage,
    page = 1,
    total = 0,
}: PaginationProps) {

    if (total == null) return null

    const pageCount = Math.max(0, Math.ceil(total / itemsPerPage))
    if (pageCount <= 1) return null

    const getRange = (start: number, end: number) => {
        return Array(end - start + 1)
            .fill(0)
            .map((_, i) => i + start)
    }

    const pagination = (currentPage: number, pageCount: number): PageItem[] => {
        let delta: number
        if (pageCount <= 7) {
            // delta === 7: [1 2 3 4 5 6 7]
            delta = 7
        } else {
            // delta === 2: [1 ... 4 5 6 ... 10]
            // delta === 4: [1 2 3 4 5 ... 10]
            delta = currentPage > 4 && currentPage < pageCount - 3 ? 2 : 4
        }

        const range = {
            start: Math.round(currentPage - delta / 2),
            end: Math.round(currentPage + delta / 2),
        }

        if (range.start - 1 === 1 || range.end + 1 === pageCount) {
            range.start += 1
            range.end += 1
        }

        let pages: PageItem[] = currentPage > delta
            ? getRange(Math.min(range.start, pageCount - delta), Math.min(range.end, pageCount))
            : getRange(1, Math.min(pageCount, delta + 1))

        const withDots = (value: number, pair: PageItem[]) => (pages.length + 1 !== pageCount ? pair : [value])

        if (pages[0] !== 1) {
            pages = withDots(1, [1, '...']).concat(pages)
        }

        const last = pages[pages.length - 1]
        if (typeof last === 'number' && last < pageCount) {
            pages = pages.concat(withDots(pageCount, ['...', pageCount]))
        }

        return pages
    }

    const pages = pagination(page, pageCount)

    const PageButton = ({ index }: { index: number }) => {
        return <button
            onClick={() => onChangePage(index)}
            className={`pagination-button ${index === page ? 'selected' : ''}`} key={index}>
            {index}
        </button>
    }

    return (
        <div className="ui-pagination">
            <button className="pagination-button prev"
                disabled={page <= 1}
                onClick={() => onChangePage(page - 1)}>
                <i className="bi bi-arrow-left"></i>
                Previous
            </button>
            {
                pages.map((curr, index) => (
                    curr === '...'
                        ? <div className="spacer" key={index}>{curr}</div>
                        : <PageButton index={curr} key={index} />
                ))
            }
            <button className="pagination-button next"
                disabled={page >= pageCount}
                onClick={() => onChangePage(page + 1)}>
                Next
                <i className="bi bi-arrow-right"></i>
            </button>
        </div>
    )
}

export default function CursorPagination({
    prevCursor,
    nextCursor,
    onPrev,
    onNext,
}: CursorPaginationProps) {
    if (!prevCursor && !nextCursor) return <></>
    return (
        <div className="ui-pagination">
            <button className="pagination-button prev"
                disabled={prevCursor === undefined}
                onClick={() => onPrev(prevCursor)}>
                <ChevronLeftIcon />
                Previous
            </button>
            <button className="pagination-button next"
                disabled={nextCursor === undefined}
                onClick={() => onNext(nextCursor)}>
                Next
                <ChevronRightIcon />
            </button>
        </div>
    )
}
